import type { FC } from 'react';
import {
    Card,
    CardGroup,
    SkeletonLine,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@george-labs.com/design-system';

import { ConditionalWrapper } from '@luna/utils-ui';

const DEFAULT_NUMBER_OF_ROWS = 4;
const DEFAULT_NUMBER_OF_COLUMNS = 4;

interface Props {
    className?: string;
    labelize?: boolean;
    numberOfRows?: number;
    numberOfColumns?: number;
    wrapWithCardGroup?: boolean;
    'data-cy'?: string;
}

const createRandomTableWidth = (numberOfColumns: number) => {
    const MAX_ROW_WIDTH = 100;
    const MIN_COLUMN_WIDTH = 10;
    const MAX_COLUMN_WIDTH = 30;

    const columnWidths = [];
    let sumOfColumnWidth = 0;

    for (let i = 0; i < numberOfColumns; i += 1) {
        const columnWidth = Math.min(
            Math.ceil((Math.random() * (MAX_ROW_WIDTH - sumOfColumnWidth)) / MIN_COLUMN_WIDTH) *
                MIN_COLUMN_WIDTH,
            MAX_COLUMN_WIDTH,
        );
        sumOfColumnWidth += columnWidth;
        columnWidths.push(columnWidth);
    }

    return columnWidths;
};

export const TableSkeleton: FC<Props> = ({
    className,
    labelize = true,
    numberOfRows = DEFAULT_NUMBER_OF_ROWS,
    numberOfColumns = DEFAULT_NUMBER_OF_COLUMNS,
    wrapWithCardGroup = true,
    'data-cy': dataCy,
}) => {
    const columns = createRandomTableWidth(numberOfColumns).map((width, i) => ({
        id: `cell-${i}`,
        label: <SkeletonLine level={5} animated />,
        labelize,
        width: `${width}%`,
    }));

    return (
        <ConditionalWrapper
            condition={wrapWithCardGroup}
            wrapper={CardGroup}
            wrapperProps={{ className }}
        >
            <Card padding={Card.PADDING.NONE} data-cy={dataCy}>
                <Table columns={columns}>
                    <TableBody>
                        {[...Array(numberOfRows).keys()].map((i) => (
                            <TableRow key={`table-skeleton-row-${i}`}>
                                {[...Array(numberOfColumns).keys()].map((j) => (
                                    <TableCell key={`table-skeleton-cell-${j}`}>
                                        <SkeletonLine level={5} animated />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </ConditionalWrapper>
    );
};
